import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";

/**
 * BrandContext — single-source-of-truth for the app-wide logo + favicon.
 *
 * • Fetches `/api/settings/brand` once on mount.
 * • Whenever `faviconUrl` changes, updates <link rel="icon"> in the
 *   document head so browsers pick up the ops upload without a hard
 *   refresh.
 * • Exposes `reload()` for the admin settings card to re-fetch after an
 *   upload without reloading the page.
 */
const BrandContext = createContext({
  logoUrl: null,
  faviconUrl: null,
  intentPickerEnabled: true,
  loading: true,
  reload: async () => {},
});

// Resolve `/api/...` paths against REACT_APP_BACKEND_URL so <img src>
// and favicon links work in preview + prod.
const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const absolutize = (path) => {
  if (!path) return null;
  if (/^https?:/i.test(path)) return path;
  return `${BACKEND}${path.startsWith("/") ? "" : "/"}${path}`;
};

export function BrandProvider({ children }) {
  const [state, setState] = useState({ logoUrl: null, faviconUrl: null, intentPickerEnabled: true, loading: true });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/brand");
      setState({
        logoUrl: absolutize(data.logo_url),
        faviconUrl: absolutize(data.favicon_url),
        // Default true so we never accidentally hide the homepage picker if
        // the endpoint omits the field (older backend, network hiccup, etc.).
        intentPickerEnabled: data.intent_picker_enabled !== false,
        loading: false,
      });
    } catch (_e) {
      // Silent — public endpoint; fall back to wordmark + default favicon.
      setState({ logoUrl: null, faviconUrl: null, intentPickerEnabled: true, loading: false });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live favicon injection.
  useEffect(() => {
    if (state.loading) return;
    const url = state.faviconUrl;
    // Remove any brand-managed favicon we previously injected.
    document.querySelectorAll("link[data-managed='brand']").forEach((n) => n.parentNode?.removeChild(n));
    if (url) {
      const link = document.createElement("link");
      link.setAttribute("rel", "icon");
      link.setAttribute("href", url);
      link.setAttribute("data-managed", "brand");
      document.head.appendChild(link);
    }
  }, [state.faviconUrl, state.loading]);

  return (
    <BrandContext.Provider value={{ ...state, reload: load }}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  return useContext(BrandContext);
}
